import React, { useMemo, useState } from 'react'
import { Box, Button, CircularProgress, Stack, useTheme } from '@mui/material'
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined'
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined'
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined'
import { useIntl } from 'react-intl'
import { useQueryClient } from '@tanstack/react-query'
import { useConfirm } from 'material-ui-confirm'

import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchCollectionWithPost } from '@app/src/api/fetchHooks'
import { useUpdateResource } from '@app/src/api/updateHooks'
import { useDrawer } from '@app/src/components/Drawer/DrawerContext'
import { useSnackbar } from '@app/src/context/SnackbarContext'
import { NotificationSeverity } from '@app/src/wf-constants'
import LoadingButton from '@app/src/components/LoadingButton'
import ConfigurationCollection from '@app/src/pages/Configurations/ConfigurationCollection'
import TemplateAvailabilityList from './TemplateAvailabilityList'
import TemplateAvailabilityDrawerView from './TemplateAvailabilityDrawerView'
import { SortOrder } from '@app/src/types/filter'
import type {
  AssessmentTemplate,
  AssessmentQuestionnaireLink,
  QuestionnaireTemplate,
} from '@app/src/types/resourceExplorer'
import type { BaseTemplate } from './availabilityHelpers'

const AssessmentTemplateAdminConfig: React.FC = () => {
  const { formatMessage } = useIntl()
  const theme = useTheme()
  const { openDrawer } = useDrawer()
  const { mutateAsync } = useUpdateResource<{ assessmentTemplateId: number }>()
  const { showSnackbar } = useSnackbar()
  const queryClient = useQueryClient()
  const confirm = useConfirm()
  const [loadingStates, setLoadingStates] = useState<{ [key: number]: boolean }>({})

  const { items: assessmentTemplates = [], isLoading: isLoadingTemplates } =
    useFetchCollectionWithPost<AssessmentTemplate>({
      endpoint: endpoints.assessmentTemplateCollection,
      key: FetchKey.AssessmentTemplateCollection,
      payload: {
        filter: [],
        include: ['organizationAvailability.organization', 'solutionAvailability'],
        sort: { target: 'name', order: SortOrder.ASCENDING },
      },
    })

  const { items: assessmentQuestionnaireLinks = [], isLoading: isLoadingLinks } =
    useFetchCollectionWithPost<AssessmentQuestionnaireLink>({
      endpoint: endpoints.assessmentQuestionnaireLink,
      key: FetchKey.AssessmentQuestionnaireLink,
      payload: {
        filter: [],
        include: [
          'questionnaireTemplate.organizationAvailability.organization',
          'questionnaireTemplate.solutionAvailability',
        ],
        sort: { target: 'questionnaireTemplateId', order: SortOrder.ASCENDING },
      },
    })

  // Map assessment templates to BaseTemplate format with their own availability
  const mappedTemplates: BaseTemplate[] = useMemo(() => {
    return assessmentTemplates.map(template => {
      const linkedQuestionnaires = assessmentQuestionnaireLinks.filter(
        link => link.assessmentTemplateId === template.id,
      )

      // Get linked questionnaires that have template data (for display with tooltips)
      const linkedTemplates = linkedQuestionnaires
        .map(link => link.questionnaireTemplate)
        .filter((q): q is QuestionnaireTemplate => q != null)
        .map(q => ({
          id: q.id,
          name: q.title,
          solutionAvailability: q.solutionAvailability,
          organizationAvailability: q.organizationAvailability,
        }))

      return {
        id: template.id,
        name: template.name,
        solutionAvailability: template.solutionAvailability,
        organizationAvailability: template.organizationAvailability,
        linkedTemplates,
      }
    })
  }, [assessmentTemplates, assessmentQuestionnaireLinks])

  const isLoading = isLoadingTemplates || isLoadingLinks

  const handleOpenAvailabilityDrawer = (template: AssessmentTemplate) => {
    openDrawer(
      <TemplateAvailabilityDrawerView
        template={{
          id: template.id,
          name: template.name,
          solutionAvailability: template.solutionAvailability,
          organizationAvailability: template.organizationAvailability,
        }}
        updateEndpoint={endpoints.updateAssessmentTemplateAvailability(template.id)}
        invalidateQueryKey={FetchKey.AssessmentTemplateCollection}
      />,
    )
  }

  const handleReEvaluate = async (template: BaseTemplate) => {
    try {
      await confirm({
        title: formatMessage({ id: 'assessments.admin.trigger' }),
        description: formatMessage({ id: 'assessments.admin.triggerDisclaimer' }),
        confirmationText: formatMessage({ id: 'general.confirm' }),
        cancellationText: formatMessage({ id: 'general.cancel' }),
      })
    } catch {
      return
    }

    setLoadingStates(prev => ({ ...prev, [template.id]: true }))

    try {
      await mutateAsync(
        {
          url: endpoints.reEvaluateAssessmentsByTemplate,
          body: { assessmentTemplateId: template.id },
        },
        {
          onSuccess: () => {
            showSnackbar({
              message: formatMessage(
                { id: 'notifications.successfullyTriggeredReEvaluatingAssessments' },
                { name: template.name },
              ),
              severity: NotificationSeverity.success,
            })
            queryClient.invalidateQueries({ queryKey: [FetchKey.AssessmentTemplateCollection] })
          },
          onError: () => {
            showSnackbar({
              message: formatMessage({
                id: 'notifications.triggeredReEvaluatingAssessmentsFailed',
              }),
              severity: NotificationSeverity.error,
            })
          },
        },
      )
    } finally {
      setLoadingStates(prev => ({ ...prev, [template.id]: false }))
    }
  }

  return (
    <ConfigurationCollection enablePadding={{ top: false, right: true }}>
      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      ) : (
        <TemplateAvailabilityList
          templates={mappedTemplates}
          icon={<FactCheckOutlinedIcon fontSize="large" />}
          avatarColor={theme.palette.secondary.main}
          linkedTemplatesLabel={formatMessage({ id: 'configurations.templateConfig.linkedQuestionnaires' })}
          linkedTemplatesIcon={<ArticleOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />}
          renderAction={template => {
            const originalTemplate = assessmentTemplates.find(t => t.id === template.id)
            if (!originalTemplate) return null
            return (
              <Stack direction="row" spacing={1} alignItems="center">
                <LoadingButton
                  variant="outlined"
                  onClick={() => handleReEvaluate(template)}
                  loading={loadingStates[template.id]}
                >
                  {formatMessage({ id: 'assessments.admin.trigger' })}
                </LoadingButton>
                <Button
                  variant="contained"
                  startIcon={<SettingsOutlinedIcon />}
                  onClick={() => handleOpenAvailabilityDrawer(originalTemplate)}
                >
                  {formatMessage({ id: 'configurations.templateConfig.availability.configure' })}
                </Button>
              </Stack>
            )
          }}
        />
      )}
    </ConfigurationCollection>
  )
}

export default AssessmentTemplateAdminConfig
